<template>
    <article class="sidebar-block" :class="rootClasses">
        <div
                role="button"
                class="sidebar-block--header"
                ref="header"
                @click="onHeaderClick">
            <h2 class="sidebar-block--title u-reset">
                {{ title }}
            </h2>

            <div class="sidebar-block--icon">
                <transition name="trs-simple-fade" mode="in-out">
                    <ui-icon class="collapsible--icon"
                            :symbol="getIcon()"
                            size="medium"
                            v-if="isBlockScrolled"/>
                </transition>
            </div>
        </div>
        <div class="sidebar-block--body-wrap" :style="bodyWrapStyle" ref="bodyWrap">
            <div class="sidebar-block--body" ref="body">
                <slot/>
            </div>
        </div>
    </article>
</template>

<script>
    import { uuidMixin, bemMixin, isMountedMixin } from '@dreipol/vue-ui/src/mixins';
    import { mapState, mapActions } from 'vuex';
    import { elementOffsetTop } from 'bianco.viewport';
    import $ from 'bianco.query';
    import { loadImages } from 'bianco.images-loader';
    import isMobileMixin from '../../mixins/is-mobile';
    import UiIcon from '@dreipol/vue-ui/src/components/icon/icon.vue';

    export default {
        components: {
            UiIcon,
        },
        mixins: [
            bemMixin('sidebar-block'),
            isMountedMixin,
            uuidMixin,
            isMobileMixin,
        ],
        props: {
            title: {
                type: String,
                required: true,
            },
            allowCollapse: {
                type: Boolean,
                default: true,
            },
        },
        computed: {
            ...mapState('viewport', {
                viewportWidth: 'width',
            }),
            ...mapState('vue-ui/scroll', {
                scrollPosition: 'position',
            }),
            ...mapState('sidebar-blocks', ['openBlockId', 'blockIds', 'blockBounds']),
            rootClasses() {
                return this.allowCollapse ? [
                    this.bemIf(this.isBlockScrolled, 'is-toggleable'),
                    this.bemIf(this.hasAnimatedHeight, 'has-animated-height'),
                    this.bemIf(this.isOpen, 'is-open'),
                ] : [];
            },
            blockIndex() {
                return this.blockIds.indexOf(this.uuid);
            },
            supportsCssSticky() {
                return typeof CSS !== 'undefined' && CSS.supports && CSS.supports('position', 'sticky');
            },
            isOpen() {
                return this.openBlockId === this.uuid || !this.supportsCssSticky;
            },
            stickyParentOffset() {
                // this is needed only to force the computed when one of the two props will end
                this.isMounted && this.viewportWidth // eslint-disable-line

                const { parentNode } = this.$el;

                parentNode.style.position = 'static';
                const offset = elementOffsetTop(parentNode);
                parentNode.style.position = '';

                return offset;
            },
            isFirstBlock() {
                return this.blockIndex === 0;
            },
            isBlockScrolled() {
                const { offsetTop } = this.bounds;

                return this.isMobile || this.scrollPosition > offsetTop && this.supportsCssSticky;
            },
            isBlockBodyScrolled() {
                const { offsetTop, bodyHeight } = this.bounds;

                return this.scrollPosition > offsetTop + bodyHeight;
            },
            hasAnimatedHeight() {
                return this.isOpen || this.isMobile || this.isBlockBodyScrolled;
            },
            previousBlockOffset() {
                // dirty hack to access to the margin bottom value of this element
                const marginBottom = Number(window.getComputedStyle(this.$el)['margin-bottom'].replace('px', ''));
                const previousBlock = this.blockBounds[this.blockIndex - 1];

                if (previousBlock) {
                    return previousBlock.offsetTop + previousBlock.height + marginBottom;
                }

                return this.stickyParentOffset;
            },
            bounds() {
                return this.blockBounds[this.uuid] || {};
            },
            bodyWrapStyle() {
                const { bodyHeight, offsetTop } = this.bounds;

                // if the block was opened we don't need to calculate its height dynamically
                switch (true) {
                    case this.isOpen:
                        return {
                            height: `${ bodyHeight }px`,
                        };
                    case this.isMobile:
                        return {
                            height: 0,
                        };
                    default:
                        return {
                            height: this.allowCollapse && this.isBlockScrolled ? `${ Math.max(0, offsetTop + bodyHeight - this.scrollPosition) }px` : '',
                        };
                }
            },
        },
        methods: {
            ...mapActions('sidebar-blocks', ['setOpenBlockId', 'setBlockId', 'setBlockBounds', 'resetBlocks']),
            onHeaderClick() {
                if (!this.isBlockScrolled) {
                    return;
                }

                this.setOpenBlockId({
                    openBlockId: this.openBlockId === this.uuid ? null : this.uuid,
                });
            },
            getBlockBounds() {
                const { header, body, bodyWrap } = this.$refs;
                const el = this.$el;

                if (!header || !body) {
                    return {};
                }

                const bodyWrapHeight = bodyWrap.style.height;
                const headerHeight = header.offsetHeight;
                const bodyHeight = body.offsetHeight;

                // calculate the bounds disabling all the custom height values
                el.style.transition = 'none';
                bodyWrap.style.height = 'auto';

                const bounds = {
                    // the offset top is computed depending on the previous block offset
                    offsetTop: this.previousBlockOffset - (this.isFirstBlock ? 0 : headerHeight),
                    height: el.offsetHeight,
                    headerHeight,
                    bodyHeight,
                };

                // restore the old css properties
                bodyWrap.style.height = bodyWrapHeight;
                el.style.transition = '';

                return bounds;
            },
            updateBlockBounds() {
                // store the block bounds into the vuex store
                this.setBlockBounds({
                    blockId: this.uuid,
                    bounds: this.getBlockBounds(),
                });
            },
            getIcon() {
                return this.isOpen ? 'collapse' : 'open';
            },
        },
        watch: {
            isBlockScrolled(value) {
                if (value === false && this.isOpen) {
                    this.setOpenBlockId({
                        openBlockId: null,
                    });
                }
            },
            viewportWidth() {
                this.updateBlockBounds();
            },
        },
        mounted() {
            const images = $('img', this.$el);
            // register this block
            this.setBlockId({
                blockId: this.uuid,
            });

            this.updateBlockBounds();

            // update the box bounds when the images are loaded
            if (images.length) {
                loadImages(images).then(this.updateBlockBounds);
            }
        },

    };
</script>
